import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats bytes into a human-readable string
 * @param bytes Number of bytes
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Fetches a PDF from a URL and performs basic validation.
 * Ensures the response is OK, has a non-zero size, is a PDF MIME type,
 * and contains the standard PDF header.
 *
 * @param url URL to fetch the PDF from
 * @throws Error if the file cannot be fetched or fails validation
 * @returns Blob of the validated PDF file
 */
export async function validateDownloadedPdf(url: string): Promise<Blob> {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error('File not found or inaccessible.')
  }

  const fileBlob = await response.blob()

  if (!fileBlob || fileBlob.size === 0) {
    throw new Error('File is empty or corrupted.')
  }

  const contentType = fileBlob.type || response.headers.get('content-type') || ''
  if (!contentType.includes('pdf') && !contentType.includes('application/pdf')) {
    throw new Error('File is not a valid PDF document.')
  }

  const arrayBuffer = await fileBlob.arrayBuffer()
  const uint8Array = new Uint8Array(arrayBuffer)
  const pdfHeader = new TextDecoder().decode(uint8Array.slice(0, 4))

  if (pdfHeader !== '%PDF') {
    throw new Error('File content is not a valid PDF.')
  }

  return fileBlob
}
